package com.schoolconnect.app.feature.rolepicker

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.material3.CircularProgressIndicator
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.checkbox_checked_role
import ischoolconnect.sharedui.generated.resources.radio_selected
import ischoolconnect.sharedui.generated.resources.radio_unselected
import ischoolconnect.sharedui.generated.resources.role_parent
import ischoolconnect.sharedui.generated.resources.role_school_admin
import ischoolconnect.sharedui.generated.resources.role_superadmin
import ischoolconnect.sharedui.generated.resources.role_teacher
import org.jetbrains.compose.resources.painterResource

private val ModalNavy = Color(0xFF07144D)
private val ModalMuted = Color(0xFF31406E)
private val ModalBorder = Color(0xFFDCE2F0)
private val ModalBlue = Color(0xFF075EF5)
private val ModalPurple = Color(0xFF6D28D9)
private val ModalOrange = Color(0xFFEA6518)
private val ModalGreen = Color(0xFF078865)
private val ModalRadioMuted = Color(0xFFA0A8C0)
private val ModalBadgeBg = Color(0xFFBFEED6)
private val ModalBadgeText = Color(0xFF08764F)
private val ModalScrim = Color(0xA20A1428)
private val ModalSurface = Color.White
private val RolePickerDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
fun RolePickerDialog(
    state: RolePickerUiState,
    onOptionSelected: (RolePickerOption) -> Unit,
    onRememberSelectionChanged: (Boolean) -> Unit,
    onContinueClicked: (RolePickerOption) -> Unit,
    onCloseClicked: () -> Unit,
    modifier: Modifier = Modifier
) {
    if (!state.isVisible) return

    val dismissHandler: () -> Unit = if (state.canDismiss) onCloseClicked else ({})
    val selectedOption = remember(state.options, state.selectedOptionId) {
        state.options.firstOrNull { it.id == state.selectedOptionId }
            ?: state.options.firstOrNull()
    }

    AppDialogScaffold(
        onDismiss = dismissHandler,
        modifier = modifier,
        size = AppDialogSize.Standard,
        colors = RolePickerDialogColors,
        maxDialogHeight = 860.dp
    ) {
        AppDialogHeader(
            title = state.title,
            subtitle = state.subtitle,
            onClose = dismissHandler,
            closeContentDescription = "Close role picker dialog",
            colors = RolePickerDialogColors,
            height = 112.dp
        )

        HorizontalDivider(color = ModalBorder)

        AppDialogBody(
            compactBreakpoint = 760.dp,
            colors = RolePickerDialogColors,
            horizontalPadding = 24.dp,
            compactHorizontalPadding = 20.dp,
            verticalPadding = 20.dp
        ) { _ ->
            val scrollState = rememberScrollState()

            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                when {
                    state.isLoading && state.options.isEmpty() -> {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(min = 260.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(14.dp)
                        ) {
                            Spacer(modifier = Modifier.height(28.dp))
                            CircularProgressIndicator(
                                color = ModalBlue,
                                strokeWidth = 3.dp
                            )
                            Text(
                                text = "Loading your authorizations...",
                                color = ModalNavy,
                                fontSize = 15.sp,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }
                    state.errorMessage.isNotBlank() && state.options.isEmpty() -> {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(min = 220.dp),
                            verticalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Text(
                                text = "Unable to load role authorizations.",
                                color = ModalNavy,
                                fontSize = 16.sp,
                                fontWeight = FontWeight.Bold
                            )
                            Text(
                                text = state.errorMessage,
                                color = ModalMuted,
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Normal,
                                maxLines = 4,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                    else -> {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 520.dp)
                                .verticalScroll(scrollState),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            state.options.forEach { option ->
                                RolePickerRow(
                                    option = option,
                                    selected = option.id == selectedOption?.id,
                                    onSelected = onOptionSelected
                                )
                            }
                        }
                    }
                }
            }
        }

        HorizontalDivider(color = ModalBorder)

        AppDialogFooter(
            actions = buildList<AppDialogFooterAction> {
                if (state.canDismiss) {
                    add(AppDialogFooterAction(label = "Cancel", onClick = dismissHandler, width = 108.dp))
                }
                add(
                    AppDialogFooterAction(
                        label = "Continue",
                        onClick = { selectedOption?.let(onContinueClicked) },
                        style = AppDialogActionStyle.Primary,
                        enabled = selectedOption != null && !state.isSubmitting,
                        width = 108.dp
                    )
                )
            },
            colors = RolePickerDialogColors,
            footerHeight = 84.dp,
            contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 24.dp),
            leadingContent = {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.Top
                ) {
                    Box(
                        modifier = Modifier
                            .size(20.dp)
                            .clip(RoundedCornerShape(3.dp))
                            .border(1.dp, ModalBlue, RoundedCornerShape(3.dp))
                            .clickable { onRememberSelectionChanged(!state.rememberSelection) },
                        contentAlignment = Alignment.Center
                    ) {
                        if (state.rememberSelection) {
                            androidx.compose.foundation.Image(
                                painter = painterResource(Res.drawable.checkbox_checked_role),
                                contentDescription = "Remember role selected",
                                modifier = Modifier.size(20.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                    }
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            text = "Remember this role for next time",
                            color = ModalNavy,
                            fontSize = 15.sp,
                            fontWeight = FontWeight.SemiBold
                        )
                        Text(
                            text = "You can switch your role anytime from Settings.",
                            color = ModalMuted,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Normal
                        )
                    }
                }
            }
        )
    }
}

@Composable
private fun RolePickerRow(
    option: RolePickerOption,
    selected: Boolean,
    onSelected: (RolePickerOption) -> Unit,
    modifier: Modifier = Modifier
) {
    val accent = roleAccent(option.roleCode)
    val roleIcon = rolePainter(option.roleCode)
    val cardBorder = if (selected) BorderStroke(2.dp, ModalBlue) else BorderStroke(1.dp, ModalBorder)
    val cardBackground = if (selected) Color(0xFFF8FBFF) else ModalSurface

    Card(
        modifier = modifier
            .fillMaxWidth()
            .height(84.dp)
            .clip(RoundedCornerShape(8.dp))
            .border(cardBorder, RoundedCornerShape(8.dp))
            .clickable { onSelected(option) }
            .semantics { role = Role.Button },
        colors = CardDefaults.cardColors(containerColor = cardBackground),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        shape = RoundedCornerShape(8.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(start = 20.dp, end = 18.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            androidx.compose.foundation.Image(
                painter = roleIcon,
                contentDescription = null,
                modifier = Modifier.size(44.dp),
                contentScale = ContentScale.Fit
            )

            Spacer(modifier = Modifier.width(20.dp))

            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = option.roleLabel,
                        color = accent,
                        fontSize = 17.sp,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                    if (option.isLastUsed && selected) {
                        androidx.compose.material3.Surface(
                            color = ModalBadgeBg,
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            Text(
                                text = "Last used",
                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                color = ModalBadgeText,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                Text(
                    text = option.schoolLabel,
                    color = ModalNavy,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            androidx.compose.foundation.Image(
                painter = painterResource(if (selected) Res.drawable.radio_selected else Res.drawable.radio_unselected),
                contentDescription = if (selected) "Selected role" else "Unselected role",
                modifier = Modifier.size(24.dp),
                contentScale = ContentScale.Fit
            )
        }
    }
}

private fun roleAccent(roleCode: String): Color = when (roleCode) {
    "2036" -> ModalPurple
    "2032" -> ModalOrange
    "2031" -> ModalGreen
    "2030" -> ModalBlue
    else -> ModalNavy
}

@Composable
private fun rolePainter(roleCode: String) = painterResource(
    when (roleCode) {
        "2036" -> Res.drawable.role_superadmin
        "2032" -> Res.drawable.role_school_admin
        "2031" -> Res.drawable.role_teacher
        "2030" -> Res.drawable.role_parent
        else -> Res.drawable.role_school_admin
    }
)
